import i18n from '../i18n';
import { START_PAGE_LOCALES, type LocalizedText, type StartPageBlock, type StartPageBlockType } from './types';

/**
 * The text to show a reader in `language`: their exact locale, then any locale of the same language, then
 * `en-US`, then whatever was authored. Null when nothing is authored, so the caller can fall back to the
 * built-in string.
 */
export function pickText(text: LocalizedText | null | undefined, language: string): string | null {
  if (!text) return null;

  const value = (locale: string) => text[locale]?.trim() || null;
  const base = baseLanguage(language);

  return value(language)
    ?? Object.keys(text).filter((locale) => baseLanguage(locale) === base).map(value).find(Boolean)
    ?? value('en-US')
    ?? Object.keys(text).map(value).find(Boolean)
    ?? null;
}

function baseLanguage(locale: string): string {
  return (locale.split('-')[0] ?? '').toLowerCase();
}

/** A built-in string in every start-page locale, from QuickDial's own translations. */
export function builtInText(key: string): LocalizedText {
  return Object.fromEntries(START_PAGE_LOCALES.map((locale) => [locale, i18n.getFixedT(locale)(key)]));
}

/** The keys of the built-in strings an empty field of a QuickDial block falls back to. */
export const BUILT_IN_TEXT_KEYS = {
  hero: {
    title: 'startPage.defaults.heroTitle',
    caption: 'startPage.defaults.heroSubtitle',
    body: 'startPage.defaults.heroBody',
    buttonLabel: 'startPage.defaults.heroButton',
  },
  features: {
    title: 'startPage.defaults.featuresTitle',
  },
  signIn: {
    title: 'startPage.defaults.signInTitle',
    body: 'startPage.defaults.signInBody',
    buttonLabel: 'startPage.defaults.signInButton',
  },
} as const satisfies Partial<Record<StartPageBlockType, Partial<Record<keyof StartPageBlock, string>>>>;

/** QuickDial's own feature cards, shown by a features block with no items of its own. */
export const BUILT_IN_FEATURES = [
  { icon: 'zap', title: 'startPage.defaults.featureSpeedDialTitle', body: 'startPage.defaults.featureSpeedDialBody' },
  { icon: 'users', title: 'startPage.defaults.featureContactsTitle', body: 'startPage.defaults.featureContactsBody' },
  { icon: 'monitor', title: 'startPage.defaults.featureTeamsTitle', body: 'startPage.defaults.featureTeamsBody' },
] as const;

/**
 * The page shown while the hosting admin has stored nothing, and the editor's starting point. The text is
 * left empty on purpose, so every reader gets the built-in strings in their own language.
 */
export function defaultStartPageBlocks(): StartPageBlock[] {
  return [
    { id: 'hero', type: 'hero' },
    { id: 'features', type: 'features', navLabel: builtInText('startPage.defaults.featuresNav') },
    { id: 'sign-in', type: 'signIn' },
  ];
}

/** The `id` attribute a block's section gets, for its header anchor link. */
export function blockAnchorId(block: StartPageBlock): string {
  return `start-${block.id}`;
}

// ---------- AppLauncher embeds ----------

export interface AppLauncherEmbedReference {
  appCode: string;
  embedId: string;
}

const IDENTIFIER = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * The app code and embed id from what an operator pastes: AppLauncher's embed snippet
 * (`<script src=".../embed.js" data-app="…" data-embed="…">`) or a frame address
 * (`/api/v1/embeds/{embedId}/frame?app={appCode}`). Null when neither can be read.
 */
export function parseAppLauncherEmbedSnippet(input: string): AppLauncherEmbedReference | null {
  const text = input.trim();
  if (!text) return null;

  const attribute = (name: string) => new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(text)?.[1]?.trim();
  const fromAttributes = { appCode: attribute('data-app'), embedId: attribute('data-embed') };
  if (fromAttributes.appCode && fromAttributes.embedId) {
    return valid(fromAttributes.appCode, fromAttributes.embedId);
  }

  try {
    const url = new URL(text);
    const match = /\/embeds\/([^/]+)\/frame\/?$/.exec(url.pathname);
    const appCode = url.searchParams.get('app');
    return match?.[1] && appCode ? valid(appCode, decodeURIComponent(match[1])) : null;
  } catch {
    return null;
  }
}

function valid(appCode: string, embedId: string): AppLauncherEmbedReference | null {
  return IDENTIFIER.test(appCode) && IDENTIFIER.test(embedId) ? { appCode, embedId } : null;
}

/**
 * The origin embed frames are loaded from: the AppLauncher portal (`VITE_APPLAUNCHER_PORTAL_URL`), whose
 * Static Web App forwards `/api` to the Function App with the frame's own `frame-ancestors` header intact.
 * The build already allows that origin in the CSP `frame-src` for the help centre, so embeds need no
 * setting of their own. Null when the build has no portal address: embed blocks are then not rendered.
 */
export function appLauncherEmbedOrigin(
  configured: string | null | undefined = import.meta.env.VITE_APPLAUNCHER_PORTAL_URL,
): string | null {
  const trimmed = configured?.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.origin : null;
  } catch {
    return null;
  }
}

/** The frame address `embed.js` would insert, built here because the CSP lets no third-party script run. */
export function appLauncherEmbedFrameUrl(
  origin: string,
  reference: AppLauncherEmbedReference,
  language: 'sv' | 'en',
): string {
  const url = new URL(`/api/v1/embeds/${encodeURIComponent(reference.embedId)}/frame`, origin);
  url.searchParams.set('app', reference.appCode);
  url.searchParams.set('lang', language);
  return url.toString();
}
